import { UseFormSetValue } from 'react-hook-form'
import i18next from 'i18next'
import { FormInputProps, SelectOption } from '@microservices/wiskey-react-components'

import { ACTION_CODE, EVENT_CODE, GENERATOR_INPUT_TYPE, MAX_INPUT_LENGTH, REGEX } from '@constants'
import { AutocompleteOption, FormCommand, ModalType } from '@types'

export type ActionForm = {
  id?: number | string
  code: string
  title: string
  jsValue: string
  actionCode?: ACTION_CODE
  actionTitle?: string
  eventCode?: EVENT_CODE | null
  eventTitle?: string
  params?: string
  columnToSort?: AutocompleteOption | null
  order?: string | number
  json?: boolean
  objectFormCode?: AutocompleteOption | null
  contextMenu?: AutocompleteOption<number> | null
  targetColumn?: AutocompleteOption | null
  formObject: AutocompleteOption | null
  command?: FormCommand[] | null
  redefinitionCommands?: FormCommand[] | null
  variable?: AutocompleteOption | null
  field?: AutocompleteOption | null
}

type useInputsParams = {
  setValue: UseFormSetValue<ActionForm>
  setIsOpenScriptValueDialog: (value: boolean) => void
  setCurrentNameScriptDialog: (value: string) => void
  actionTypeOptions: SelectOption[]
  eventOptions: SelectOption[]
  objectOptions: AutocompleteOption[]
  watchJson?: boolean
  watchEvent?: EVENT_CODE
  watchActionCode?: ACTION_CODE
  watchFormObject?: AutocompleteOption | null
  formCodeOptions: AutocompleteOption[]
  loadingAllForm?: boolean
  loadingBindingValuesEvents?: boolean
  isAllObjectsLoading: boolean
  modalType: ModalType
  variablesOptions: AutocompleteOption[]
  fieldsOptions: AutocompleteOption[]
}

export const useInputs = ({
  setValue,
  setIsOpenScriptValueDialog,
  setCurrentNameScriptDialog,
  actionTypeOptions,
  eventOptions,
  objectOptions,
  watchJson,
  watchActionCode,
  watchFormObject,
  formCodeOptions,
  loadingAllForm,
  loadingBindingValuesEvents,
  isAllObjectsLoading,
  modalType,
  watchEvent,
  fieldsOptions,
  variablesOptions,
}: useInputsParams) => {
  const isEmptyActionCode = !watchActionCode

  const actionValueInputs: FormInputProps[] = [
    {
      name: 'formObject',
      inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
      label: i18next.t('actionInputs.formObject.label'),
      placeholder: i18next.t('actionInputs.formObject.placeholder'),
      autocompleteOptions: objectOptions,
      loading: isAllObjectsLoading,
      rules: {
        required: true,
        validate: (value: string) => value !== i18next.t('actionInputs.formObject.placeholder'),
      },
      onChangeAutocomplete: () => {
        setValue('objectFormCode', null)
      },
    },
    {
      name: 'objectFormCode',
      inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
      placeholder: i18next.t('placeholder.objectFormCode'),
      label: i18next.t('label.objectFormCode'),
      autocompleteOptions: formCodeOptions,
      loading: loadingAllForm,
      rules: { required: true },
      labelSx: { ml: 1 },
      disabled: !watchFormObject,
    },
  ]

  const actionJSONValueInputs: FormInputProps[] = [
    {
      name: 'json',
      inputType: GENERATOR_INPUT_TYPE.CHECKBOX,
      label: i18next.t('label.json'),
      labelPlacement: 'end',
    },
    ...(watchJson
      ? [
          {
            name: 'params',
            inputType: GENERATOR_INPUT_TYPE.TEXTAREA,
            label: i18next.t('actionInputs.params.label'),
            placeholder: i18next.t('actionInputs.params.placeholder'),
            rules: { required: true, validate: (value: string) => value.trim().length !== 0 },
          },
        ]
      : []),
  ]

  const actionInputs: FormInputProps[] = [
    {
      name: 'code',
      inputType: GENERATOR_INPUT_TYPE.INPUT,
      placeholder: i18next.t('actionInputs.code.placeholder'),
      replacePattern: REGEX.MODEL_CODE_REPLACE_PATTERN,
      label: i18next.t('actionInputs.code.label'),
      disabled: modalType === 'edit',
      rules: { required: true, validate: value => value.trim().length !== 0 },
    },
    {
      name: 'title',
      inputType: GENERATOR_INPUT_TYPE.INPUT,
      placeholder: i18next.t('actionInputs.title.placeholder'),
      label: i18next.t('actionInputs.title.label'),
      rules: { required: true, validate: value => value.trim().length !== 0 },
    },
    {
      name: 'actionCode',
      inputType: GENERATOR_INPUT_TYPE.SELECT,
      label: i18next.t('actionInputs.actionCode.label'),
      placeholder: i18next.t('placeholder.actionType'),
      selectOptions: actionTypeOptions,
      rules: { required: true },
      onChangeSelect: value => {
        if (value !== watchActionCode) {
          setValue('eventCode', null, { shouldDirty: true })
        }

        setValue('objectFormCode', null)
        setValue('formObject', null)
        setValue('redefinitionCommands', [])
      },
    },
    {
      name: 'eventCode',
      inputType: GENERATOR_INPUT_TYPE.SELECT,
      label: i18next.t('actionInputs.eventCode.label'),
      placeholder: i18next.t('placeholder.event'),
      selectOptions: isEmptyActionCode ? [] : eventOptions,
      loading: loadingBindingValuesEvents,
      rules: { required: true },
    },
    ...(watchEvent === EVENT_CODE.CHANGE_FIELD_VALUE ||
    watchActionCode === ACTION_CODE.SET_FIELD_VALUE
      ? [
          {
            name: 'field',
            disableClearable: true,
            inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
            label: i18next.t('actionInputs.field.label'),
            autocompleteOptions: fieldsOptions,
            loading: loadingAllForm,
            rules: { required: true },
          },
        ]
      : []),
    ...(watchActionCode === ACTION_CODE.CHANGE_VARIABLE_VALUE ||
    watchEvent === EVENT_CODE.ON_VARIABLE_CHANGE
      ? [
          {
            name: 'variable',
            disableClearable: true,
            inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
            label: i18next.t('actionInputs.variable.label'),
            autocompleteOptions: variablesOptions,
            rules: { required: true },
          },
        ]
      : []),
    ...(watchActionCode === ACTION_CODE.SET_FIELD_VALUE ||
    watchActionCode === ACTION_CODE.EXECUTE_COMMAND ||
    watchActionCode === ACTION_CODE.CHANGE_VARIABLE_VALUE
      ? [
          {
            disableClearable: true,
            inputType: GENERATOR_INPUT_TYPE.INPUT,
            label: i18next.t('actionInputs.jsValue.label'),
            labelSx: { minWidth: 120 },
            name: 'jsValue',
            placeholder: i18next.t('actionInputs.jsValue.placeholder'),
            readOnly: true,
            additionalBtn: {
              isEnabled: true,
              text: 'edit',
              color: 'primary' as const,
              variant: 'contained' as const,
              onClick: () => {
                setIsOpenScriptValueDialog(true)
                setCurrentNameScriptDialog('jsValue')
              },
            },
            formInputContainerSx: {
              flexWrap: 'nowrap',
              '& .MuiFormControlLabel-root': {
                mb: 1,
              },
              '& .MuiButton-root': {
                ml: 0.5,
              },
            },
            rules: {
              maxLength: MAX_INPUT_LENGTH,
            },
          },
        ]
      : []),
  ]

  return { actionInputs, actionValueInputs, actionJSONValueInputs }
}
